/**
 * CourseSpecificInfo Component
 *
 * Displays a detailed section with course information, including trailer, image, user progress, diploma, and course items.
 * Integrates Redux selectors for course and user info, and manages trailer modal state.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Conditional rendering of trailer button and modal if available
 * - Displays course image and user progress bar
 * - Renders diploma only if all activities are completed and user info exists
 * - Passes props to child components for modularity and maintainability
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.dataCourse - Data object containing course info, trailer, user progress, lessons, and diploma
 * @returns {JSX.Element} The rendered course information section
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Button from '@mui/material/Button';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';

import BtnShop from '../BtnShop/BtnShop';
import ItemsOfTheCourse from './ItemsOfTheCourse/ItemsOfTheCourse';
import ShowDiploma from './ShowDiploma/ShowDiploma';
import ShowTrailer from './ShowTrailer/ShowTrailer';
import UserProgressBar from './UserProgressBar/UserProgressBar';

import classes from './CourseSpecificInfo.module.css';

const url = window.location.pathname.split('/');

const CourseSpecificInfo = ({ dataCourse }) => {
  const courseInfo = useSelector(state => state.courseInfo);
  const userInfo = useSelector(state => state.userInfo);
  const [openTrailer, setOpenTrailer] = useState(false);

  /**
   * Handles opening and closing the trailer modal
   */
  const openPreviewVideoHandler = () => {
    setOpenTrailer(!openTrailer);
  };

  return (
    <aside
      className={
        courseInfo.accessCourse
          ? url[2] !== 'course'
            ? classes.specificInfoAccessContainer
            : classes.specificInfoAccessContainerUrl
          : classes.specificInfoContainer
      }
    >
      {/* Trailer button, only if trailer is available */}
      {dataCourse.trailerCourse.httpStatus === 200 && (
        <Button
          onClick={openPreviewVideoHandler}
          className={
            courseInfo.accessCourse
              ? classes.specificTrailerBtn
              : classes.specificTrailerBigBtn
          }
        >
          <PlayCircleFilledIcon
            className={classes.playTrailerIcon}
          />
          <p className={classes.trailerText}>
            Ver trailer de este curso
          </p>
        </Button>
      )}
      {/* Course image */}
      <figure
        className={
          courseInfo.accessCourse
            ? classes.specificInfoAccessImage
            : classes.specificInfoImage
        }
      >
        <img
          className={classes.img}
          src={dataCourse.data.imgPathBig}
          alt=""
        />
        {/* Props passed: course image source and styling */}
      </figure>
      {/* User progress bar, only if user has access */}
      {courseInfo.accessCourse && (
        <UserProgressBar
          userAditionalTrack={dataCourse.userAditionalTrack} // Prop passed: user activity tracking
          lessons={dataCourse.lessonsCourse} // Prop passed: lessons data
        />
      )}
      {/* Shop button */}
      <BtnShop />
      {/* Course items, only if user does not have access */}
      {!courseInfo.accessCourse && (
        <ItemsOfTheCourse dataCourse={dataCourse} /> // Prop passed: course data
      )}
      {/* Diploma, only if all activities are completed and user info exists */}
      {courseInfo.missingActivities.length === 0 &&
        userInfo.data.usuario.length !== 0 && (
          <ShowDiploma
            dataCertificate={dataCourse.userDegree} // Prop passed: diploma data
            titleCourse={courseInfo.data.nombre} // Prop passed: course title
          />
        )}
      {/* Trailer modal, only if trailer is available */}
      {dataCourse.trailerCourse.httpStatus === 200 && (
        <ShowTrailer
          openTrailer={openTrailer} // Prop passed: modal open state
          onClose={openPreviewVideoHandler} // Prop passed: close handler
          trailerData={dataCourse.trailerCourse.response} // Prop passed: trailer video data
        />
      )}
    </aside>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CourseSpecificInfo.propTypes = {
  /**
   * Data object containing course info, trailer, user progress, lessons, and diploma
   * Passed as prop to render course information
   */
  dataCourse: PropTypes.shape({
    trailerCourse: PropTypes.shape({
      httpStatus: PropTypes.number,
      response: PropTypes.object
    }),
    data: PropTypes.shape({
      imgPathBig: PropTypes.string,
      nombre: PropTypes.string
    }),
    userAditionalTrack: PropTypes.object,
    lessonsCourse: PropTypes.object,
    userDegree: PropTypes.object
  })
};

CourseSpecificInfo.defaultProps = {
  dataCourse: {
    trailerCourse: { httpStatus: 0, response: {} },
    data: { imgPathBig: '', nombre: '' },
    userAditionalTrack: {},
    lessonsCourse: {},
    userDegree: {}
  }
};

export default CourseSpecificInfo;
